<template>
    <v-dialog v-model="dialogSwitch" persistent scrollable width="1100">
        <v-card>
            <v-card-title id="heading" class="d-flex justify-space-between">
                <h4>"{{ asset.label }}" Alerts</h4>
                <div class="d-flex align-center ga-2">
                    <v-btn color="green" prepend-icon="mdi-check" size="x-small" variant="tonal"
                           :disabled="!alerts.length" @click="readAll">Mark all Read
                    </v-btn>
                    <v-btn color="red" prepend-icon="mdi-close" size="x-small" variant="tonal"
                           :disabled="!alerts.length" @click="clearAll">Clear All
                    </v-btn>
                    <v-btn density="compact" icon="mdi-close" variant="flat" @click="$emit('update:modelValue',false)"/>
                </div>
            </v-card-title>

            <v-container>
                <v-row>
                    <v-col cols="12">
                        <v-table>
                            <thead>
                            <tr>
                                <th>ID</th>
                                <th>Alert Type</th>
                                <th>Priority</th>
                                <th>App</th>
                                <th>SMS</th>
                                <th>Email</th>
                                <th>Event Timestamp</th>
                                <th>Payload</th>
                                <th>Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="alert in alerts" v-if="!loading && alerts.length" :key="alert.id">
                                <td>{{ alert.id }}</td>
                                <td>{{ alertLabel(alert) }}</td>
                                <td>{{ alert.priority }}</td>
                                <td>
                                    <v-icon :icon="`mdi-${alert.app?'check':'close'}-circle`" size="small"/>
                                </td>
                                <td>
                                    <v-icon :icon="`mdi-${alert.sms?'check':'close'}-circle`" size="small"/>
                                </td>
                                <td>
                                    <v-icon :icon="`mdi-${alert.email?'check':'close'}-circle`" size="small"/>
                                </td>
                                <td>{{ formatDateTime(alert.event_timestamp) }}</td>
                                <td>
                                    <v-icon icon="mdi-information" @click="showPayload(alert)"/>
                                </td>
                                <td style="white-space: nowrap;">
                                    <v-btn icon="mdi-delete-outline" title="Delete" size="x-small" variant="text"
                                           color="red" @click="deleteAlert(alert)"/>
                                </td>
                            </tr>
                            <tr v-else class="text-center">
                                <td v-if="loading" colspan="9">
                                    <v-progress-circular color="primary" indeterminate/>
                                </td>
                                <td v-else colspan="9">No record found</td>
                            </tr>
                            </tbody>
                        </v-table>
                    </v-col>
                </v-row>

                <v-row v-if="totalPages > 1" justify="center">
                    <v-col cols="8">
                        <v-container class="max-width">
                            <v-pagination
                                v-model="currentPage"
                                :length="totalPages"
                                class="my-4"
                                density="compact"
                                @update:model-value="fetchAlerts"
                            ></v-pagination>
                        </v-container>
                    </v-col>
                </v-row>
            </v-container>

            <v-card-actions class="px-4 d-flex justify-end">
                <v-btn size="small" type="button" @click="$emit('update:modelValue',false)">
                    Close
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>


    <v-dialog v-if="payloadDialogSwitch && selectedAlert" v-model="payloadDialogSwitch" max-width="700" scrollable>
        <v-card>
            <v-card-title class="d-flex justify-space-between">
                <span>
                    <span class="text-caption text-grey me-1">#{{ selectedAlert.id }}</span>
                    {{ alertLabel(selectedAlertAsNotification) }}
                </span>
                <v-btn density="compact" icon="mdi-close" variant="flat" @click="payloadDialogSwitch = false"/>
            </v-card-title>
            <v-card-text class="pa-2">
                <alert-payload-details :notification="selectedAlertAsNotification"/>
            </v-card-text>
        </v-card>
    </v-dialog>

</template>

<script setup>
import {ref, computed, onMounted} from "vue";
import {send} from "@/composables/apiClient";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {formatDateTime} from "@/composables/useTimezone";
import AlertPayloadDetails from "@/components/cards/AlertPayloadDetails.vue";
import {alertLabel} from "@/composables/alertTypes";

const snackbar = useSnackbarStore();
const selectedAlert = ref(null);
const currentPage = ref(1);
const totalPages = ref(1);
const loading = ref(true);
const alerts = ref([]);
const props = defineProps({
    modelValue: Boolean,
    asset: {
        type: Object
    }
});

const emits = defineEmits(['update:modelValue']);
const payloadDialogSwitch = ref(false);
const dialogSwitch = ref(props.modelValue);

onMounted(() => {
    fetchAlerts()
});
const selectedAlertAsNotification = computed(() => {
    const alert = selectedAlert.value;
    if (!alert) return null;
    let payload = alert.payload;
    if (typeof payload === 'string') {
        try { payload = JSON.parse(payload); } catch { payload = {}; }
    }
    return {
        ...alert,
        payload,
        monitoring_config_name: alert.monitoring_config_name || alert.monitoring_config || null,
        device_slno: alert.device_slno || alert.slno || null,
        asset_id: alert.asset_id || payload?.asset_id || null,
    };
});

const showPayload = (alert) => {
    selectedAlert.value = alert;
    payloadDialogSwitch.value = true;
}
const fetchAlerts = () => {
    loading.value = true;
    send('get', `assets/alerts`, {asset: props.asset.id, page: currentPage.value})
        .then((response) => {
            alerts.value = response.data.data.alerts.data;
            totalPages.value = response.data.data.alerts.last_page;
            currentPage.value = response.data.data.alerts.current_page;
        })
        .catch((error) => {
            console.log(error)
            snackbar.showSnackbar('Asset alerts could not be loaded', 'error')
        }).finally(() => loading.value = false)
}
const deleteAlert = (alert) => {
    if (confirm('Delete this alert?')) {
        send('delete', `assets/alerts/${alert.id}`)
            .then((response) => {
                if (response.data.success) {
                    alerts.value = alerts.value.filter(a => a.id !== alert.id);
                    snackbar.showSnackbar(response.data.message);
                }
            })
            .catch(() => snackbar.showSnackbar('Could not delete alert', 'error'));
    }
}
const readAll = () => {
    send('post', `notifications/read`, {asset_id: props.asset.id})
        .then((response) => {
            if (response.data.success) {
                fetchAlerts();
                snackbar.showSnackbar(response.data.message);
            }
        })
        .catch(() => snackbar.showSnackbar('Could not mark all as read', 'error'));
}
const clearAll = () => {
    if (confirm('Clear all alerts for this asset?')) {
        send('delete', `notifications/clear`, {params: {asset_id: props.asset.id}})
            .then((response) => {
                if (response.data.success) {
                    fetchAlerts();
                    snackbar.showSnackbar(response.data.message);
                }
            })
            .catch(() => snackbar.showSnackbar('Could not clear alerts', 'error'));
    }
}

</script>

